import { OverlayModule } from '@angular/cdk/overlay';
import { provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import type { ApplicationConfig } from '@angular/core';
import { APP_ID, APP_INITIALIZER, ErrorHandler, importProvidersFrom, provideZoneChangeDetection, TransferState } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import type { ExtraOptions } from '@angular/router';
import { Router, RouterModule } from '@angular/router';
import { LocalizationApiService } from '@dev-fast/backend-services';
import {
  BB_ROUTES,
  CURRENCY_ENGINE,
  GIFT_PROVIDERS_TOKEN,
  LOBBY_SEO_PAGES_TOKEN,
  LOGIN_PROVIDERS_TOKEN,
  SEO_DATA_TOKEN,
} from '@dev-fast/types';
import type { TranslateModuleConfig } from '@ngx-translate/core';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import type { NgxsDevtoolsOptions } from '@ngxs/devtools-plugin';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import type { NgxsLoggerPluginOptions } from '@ngxs/logger-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsRouterPluginModule, RouterStateSerializer } from '@ngxs/router-plugin';
import type { NgxsModuleOptions } from '@ngxs/store';
import { NgxsModule } from '@ngxs/store';
import { NgxsActionsExecutingModule } from '@ngxs-labs/actions-executing';
import * as Sentry from '@sentry/angular';
import { CAPTCHA_CONFIG } from 'hcaptcha';
import type { SocketIoConfig } from 'ngx-socket-io';
import { SocketIoModule } from 'ngx-socket-io';
import { withNgxsResetPlugin } from 'ngxs-reset-plugin';
import { provideChunkErrorRecovery } from 'sw-service';

import { AuthState } from '@app/auth';
import { CurrencyService } from '@app/core/currency';
import { customTranslateLoader } from '@app/core/custom-trans-loader';
import { ENVIRONMENTS, LOCATION } from '@app/core/environment-service';
import { IFrameCommonService, IFrameMessageService, IFrameState } from '@app/core/iframe';
import { httpInterceptorProviders } from '@app/core/interceptors';
import { MarketingParamsService } from '@app/core/marketing-params-service';
import { SEO_ROUTES } from '@app/core/seo-service';
import { FaqState } from '@app/core/state/faq';
import { CustomRouterStateSerializer } from '@app/core/state/utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ShootingEventState } from '@app/modules/events/lib/black-friday-event/state';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { NyAllEventService, NyAllEventState, StandardEventService, StandardEventState } from '@app/modules/events/lib/core';
import { provideSeasonsRightSidebarWidget } from '@app/modules/seasons/root';
import { ConfirmDialogService } from '@app/shared/modules';
import { DAILY_QUEST_EVENT_CONFIG, IS_SERVER_TOKEN, SHOW_CASE_PRICE, SOCIALS_IN_FOOTER_TOKEN } from '@app/shared/utils';
import { SHOOTING_EVENT_CONFIG } from '@app/shared/utils/events.token';
import { createSentryErrorHandler, createSentryTraceService } from '@app/shared/utils/sentry';
import { provideWindow } from '@app/shared/utils/window';
import { SPECIAL_ICONS } from '@app/ui/icons';
import { RECENT_ACTIVITIES_ENGINE, RecentActivitiesService } from '@app/widgets/recent-activities';

import { customIcons } from '../constants/custom-icons.const';
import { DailyQuestEventConfig } from '../constants/daily-quest-event-config.const';
import { GIFT_PROVIDERS } from '../constants/gift-providers.const';
import { ShootingEventConfig } from '../constants/shooting-event-config.const';
import { LOGIN_PROVIDERS, SOCIALS_MEDIA } from '../constants/socials.const';
import { bbConfig, environment, location } from '../environments/environment';
import { getAppLobbySeoPages, getAppSeoRoutes } from './app-seo-data.util';
import { STATES } from './core-states';
import { routes } from './root.routes';

const socketConfig: SocketIoConfig = {
  url: environment.WS_GATEWAY_URL,
  options: {
    reconnection: true,
    reconnectionDelay: 1000,
    transports: ['websocket'],
  },
};

const scrollExcludes: string[] = ['case_category'];

const routerConfig: ExtraOptions = {
  get scrollPositionRestoration() {
    if (typeof document === 'undefined') {
      return 'enabled';
    }
    const params = new URLSearchParams(document.location.search);
    const disabledScroll = scrollExcludes.some((param) => params.get(param));
    return disabledScroll ? 'disabled' : 'enabled';
  },
  anchorScrolling: 'enabled',
  initialNavigation: 'enabledBlocking',
  bindToComponentInputs: true,
};

const translateOptions: TranslateModuleConfig = {
  // useDefaultLang: true,
  // defaultLanguage: 'default-locale',
  loader: {
    provide: TranslateLoader,
    useFactory: customTranslateLoader,
    deps: [LocalizationApiService, TransferState],
  },
  isolate: false,
};

const ngxsLoggerOptions: NgxsLoggerPluginOptions = {
  collapsed: true,
  disabled: environment.production || !environment.LOG_ACTIONS,
};

const ngxsOptions: NgxsModuleOptions = {
  developmentMode: !environment.production,
};

const ngxsReduxDevtoolsOptions: NgxsDevtoolsOptions = {
  disabled: environment.production,
};

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: APP_ID,
      useValue: 'serverApp',
    },
    {
      provide: ErrorHandler,
      useFactory: (isServer: boolean) =>
        createSentryErrorHandler(isServer, {
          showDialog: false,
        }),
      deps: [IS_SERVER_TOKEN],
    },
    {
      provide: Sentry.TraceService,
      useFactory: (router: Router, isServer: boolean) => createSentryTraceService(router, isServer),
      deps: [Router, IS_SERVER_TOKEN],
    },
    {
      provide: APP_INITIALIZER,
      deps: [MarketingParamsService, IS_SERVER_TOKEN],
      useFactory: (service: MarketingParamsService, isServer: boolean) => {
        return () => {
          if (isServer) {
            return;
          }
          const params = new URLSearchParams(window.location.search);
          //Вызывается для получения необходимых params для Маркетинговых лендингов
          service.processCurrentQueryParams(params);
        };
      },
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => null,
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideChunkErrorRecovery(),
    ...httpInterceptorProviders(environment.production),
    importProvidersFrom(
      NgxsModule.forRoot([AuthState, FaqState, IFrameState, ...STATES], ngxsOptions),
      NgxsRouterPluginModule.forRoot(),
      RouterModule.forRoot(routes, routerConfig),
      NgxsActionsExecutingModule.forRoot(),
      SocketIoModule.forRoot(socketConfig),
      OverlayModule,
      MatTooltip,
      MatDialogModule,
      MatMenuModule,
      NgxsFormPluginModule.forRoot(),
      NgxsReduxDevtoolsPluginModule.forRoot(ngxsReduxDevtoolsOptions),
      NgxsLoggerPluginModule.forRoot(ngxsLoggerOptions),
      NgxsModule.forFeature([StandardEventState, NyAllEventState, ShootingEventState]),
    ),
    provideTranslateService(translateOptions),
    IFrameCommonService,
    IFrameMessageService,
    RecentActivitiesService,
    StandardEventService,
    NyAllEventService,
    ConfirmDialogService,
    {
      provide: RECENT_ACTIVITIES_ENGINE,
      useClass: RecentActivitiesService,
    },

    { provide: RouterStateSerializer, useClass: CustomRouterStateSerializer },
    {
      provide: ENVIRONMENTS,
      useValue: environment,
    },
    {
      provide: LOCATION,
      useValue: location,
    },
    {
      provide: SEO_ROUTES,
      useValue: getAppSeoRoutes(environment),
    },
    {
      provide: BB_ROUTES,
      useValue: bbConfig,
    },
    {
      provide: IS_SERVER_TOKEN,
      useValue: false,
    },
    {
      provide: CAPTCHA_CONFIG,
      useValue: { siteKey: environment.HCAPTCHA_SITEKEY },
    },
    {
      provide: SHOW_CASE_PRICE,
      useValue: false,
    },
    {
      provide: SEO_DATA_TOKEN,
      useValue: { title: 'SEO.TITLE.UNIVERSAL', description: 'SEO.DESCRIPTION.DEFAULT' },
    },
    {
      provide: LOGIN_PROVIDERS_TOKEN,
      useValue: LOGIN_PROVIDERS,
    },
    {
      provide: SOCIALS_IN_FOOTER_TOKEN,
      useValue: SOCIALS_MEDIA,
    },
    {
      provide: GIFT_PROVIDERS_TOKEN,
      useValue: GIFT_PROVIDERS,
    },
    {
      provide: SPECIAL_ICONS,
      useValue: customIcons,
    },
    {
      provide: DAILY_QUEST_EVENT_CONFIG,
      useValue: DailyQuestEventConfig,
    },
    {
      provide: SHOOTING_EVENT_CONFIG,
      useValue: ShootingEventConfig,
    },
    {
      provide: CURRENCY_ENGINE,
      useClass: CurrencyService,
    },
    {
      provide: LOBBY_SEO_PAGES_TOKEN,
      useValue: getAppLobbySeoPages(environment),
    },
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    withNgxsResetPlugin(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),
    provideWindow(),
    provideSeasonsRightSidebarWidget(),
    // provideClientHydration(withEventReplay()),
  ],
};
